import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Scissors, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DISPOSITIONS = [
  { v: "continue", label: "Üretime Devam" },
  { v: "holdback", label: "Yarı Mamul" },
  { v: "waste", label: "Fire" },
];

// Kesim Kaydı — fazla kesim backend'ini (POST /departments/jobs/{id}/cutting) REUSE eder.
// Sipariş miktarı / satış / fatura DEĞİŞMEZ; yalnızca kesim department_job'ına yazar.
export default function CuttingRecordDialog({ open, onOpenChange, order, onSaved }) {
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState("");
  const [disp, setDisp] = useState("continue");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open || !order?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: j } = await api.post("/departments/jobs/ensure", {
          order_id: order.id,
          department: "kesim",
        });
        if (cancelled) return;
        setJob(j);
        const existing = j.cutting?.lines || [];
        const vb = j.variant_breakdown || [];
        const seeded = existing.length
          ? existing.map((li) => ({
              line_id: li.line_id || "",
              variant_id: li.variant_id || "", size: li.size || "", color: li.color || "",
              product_name: li.product_name || "", planned_qty: li.planned_qty, actual_qty: li.actual_qty,
            }))
          : (vb.length
              ? vb.map((li) => ({
                  line_id: li.line_id || "",
                  variant_id: li.variant_id || "", size: li.size || "", color: li.color || "",
                  product_name: li.product_name || "", planned_qty: li.quantity, actual_qty: li.quantity,
                }))
              : [{ line_id: "", variant_id: "", size: "-", color: "-", product_name: j.product_name || "",
                   planned_qty: j.quantity || 0, actual_qty: j.quantity || 0 }]);
        setRows(seeded);
        setReason(j.cutting?.overcut_reason || "");
        setDisp(j.cutting?.disposition || "continue");
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id]);

  const totals = useMemo(() => {
    const planned = rows.reduce((s, r) => s + Number(r.planned_qty || 0), 0);
    const actual = rows.reduce((s, r) => s + Number(r.actual_qty || 0), 0);
    return { planned, actual, overcut: Math.max(0, actual - planned) };
  }, [rows]);

  const save = async () => {
    if (!job) return;
    setSaving(true);
    try {
      const payload = {
        breakdown: rows.map((r) => ({
          line_id: r.line_id || "",
          variant_id: r.variant_id, size: r.size, color: r.color,
          product_name: r.product_name,
          planned_qty: Number(r.planned_qty || 0),
          actual_qty: Number(r.actual_qty || 0),
        })),
        overcut_reason: totals.overcut > 0 ? reason : "",
        disposition: totals.overcut > 0 ? disp : null,
      };
      const { data: savedJob } = await api.post(`/departments/jobs/${job.id}/cutting`, payload);
      toast.success(totals.overcut > 0 ? `Kesim kaydedildi (+${totals.overcut} fazla)` : "Kesim kaydedildi");
      // POST güncel job'ı döner — "Siparişe Uygula" butonu buna göre açılır
      if (savedJob) setJob(savedJob);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const canApply = !!job?.cutting?.lines?.length
    && job.cutting.disposition === "continue"
    && Number(job.cutting.overcut_qty || 0) > 0;
  const alreadyApplied = canApply && !!job.cutting.applied_to_order
    && Number(job.cutting.applied_actual_qty || 0) === Number(job.cutting.actual_qty || 0);

  const applyToOrder = async () => {
    if (!job) return;
    if (!window.confirm(
      `Kesilen toplam (${job.cutting.actual_qty}) sipariş miktarına uygulanacak.\n` +
      `Planlanan (${job.cutting.planned_qty}) geçmişte korunur. Birim fiyat ve KDV değişmez.\nOnaylıyor musunuz?`
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/departments/jobs/${job.id}/cutting/apply`, {});
      setJob({ ...job, cutting: { ...job.cutting, applied_to_order: true, applied_actual_qty: job.cutting.actual_qty } });
      toast.success(`${data.order_no}: sipariş adedi ${data.prev_quantity} → ${data.quantity}`);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cutting-dialog" className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <Scissors className="w-4 h-4 text-amber-400" />
            Kesim Kaydı — {order?.order_no}
          </DialogTitle>
          <div className="text-[11px] text-slate-500 font-mono">
            {order?.customer_name} · Sipariş adedi değişmez
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-sm border border-slate-800 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-950 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                <div className="col-span-4">Ürün</div>
                <div className="col-span-2">Renk</div>
                <div className="col-span-1">Beden</div>
                <div className="col-span-2 text-right">Planlanan</div>
                <div className="col-span-2 text-right">Kesilen</div>
                <div className="col-span-1 text-right">Fazla</div>
              </div>
              <div className="max-h-72 overflow-y-auto kanban-scroll">
                {rows.map((r, idx) => {
                  const diff = Number(r.actual_qty || 0) - Number(r.planned_qty || 0);
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-800 items-center">
                      <div className="col-span-4 text-xs text-slate-200 line-clamp-2" title={r.product_name}>{r.product_name || "—"}</div>
                      <div className="col-span-2 text-xs text-slate-400">{r.color || "—"}</div>
                      <div className="col-span-1 text-xs font-semibold text-slate-100">{r.size || "—"}</div>
                      <div className="col-span-2 text-right text-slate-300 tabular-nums font-mono text-sm" data-testid={`cutting-planned-${idx}`}>{r.planned_qty}</div>
                      <div className="col-span-2">
                        <Input
                          data-testid={`cutting-actual-${idx}`}
                          type="number" min="0"
                          value={r.actual_qty}
                          onChange={(e) => {
                            const next = [...rows];
                            next[idx] = { ...next[idx], actual_qty: e.target.value };
                            setRows(next);
                          }}
                          className="bg-slate-950 border-slate-700 rounded-sm text-right font-mono h-8"
                        />
                      </div>
                      <div className={`col-span-1 text-right tabular-nums font-mono text-sm ${diff > 0 ? "text-amber-400" : diff < 0 ? "text-red-400" : "text-slate-500"}`} data-testid={`cutting-diff-${idx}`}>
                        {diff > 0 ? `+${diff}` : diff}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-800 bg-slate-950/60 text-xs font-mono">
                <div className="col-span-7 uppercase tracking-widest text-slate-400">Toplam</div>
                <div className="col-span-2 text-right tabular-nums text-slate-200" data-testid="cutting-total-planned">{totals.planned}</div>
                <div className="col-span-2 text-right tabular-nums text-slate-200" data-testid="cutting-total-actual">{totals.actual}</div>
                <div className={`col-span-1 text-right tabular-nums ${totals.overcut > 0 ? "text-amber-400" : "text-slate-500"}`} data-testid="cutting-total-overcut">
                  {totals.overcut > 0 ? `+${totals.overcut}` : "0"}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                Kesim Notu {totals.overcut > 0 && <span className="text-amber-400">(fazla kesimde zorunlu)</span>}
              </label>
              <Textarea
                data-testid="cutting-reason"
                rows={2}
                placeholder="Örn: Pastal denk gelmediği için 5 adet fazla kesildi."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm resize-none text-xs"
              />
            </div>

            {totals.overcut > 0 && (
              <div className="space-y-2 rounded-sm border border-amber-900/60 bg-amber-950/20 p-3">
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3" /> Fazla Kesim +{totals.overcut} — Karar Gerekli
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {DISPOSITIONS.map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`cutting-disposition-${opt.v}`}
                      onClick={() => setDisp(opt.v)}
                      className={`px-2 py-1.5 rounded-sm text-[11px] uppercase tracking-wider font-semibold transition-colors ${
                        disp === opt.v
                          ? "bg-amber-600 text-amber-950"
                          : "bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-800"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Fazla adet üretim takibinde ayrı izlenir. Sipariş adedi yalnızca "Siparişe Uygula" onayıyla güncellenir; birim fiyat ve KDV değişmez.
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">Kapat</Button>
          {alreadyApplied && (
            <span data-testid="cutting-applied-badge" className="text-[11px] text-emerald-400 font-mono flex items-center gap-1 mr-auto">
              ✓ Siparişe uygulandı ({job.cutting.actual_qty} ad)
            </span>
          )}
          <Button data-testid="save-cutting-btn" disabled={saving || loading} onClick={save} className="bg-amber-600 hover:bg-amber-500 text-amber-950 rounded-sm uppercase tracking-wider text-xs font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kesim Kaydet"}
          </Button>
          {canApply && !alreadyApplied && (
            <Button data-testid="apply-cutting-btn" disabled={applying || loading || saving} onClick={applyToOrder} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Siparişe Uygula (Kesilen: ${job.cutting.actual_qty})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
